import { useEffect, useRef } from "react";
import { useRouter } from "next/router";

import { Api } from "~/api";
import {
  collectPageMeta,
  metaSnapshotKey,
} from "~/utils/seo/collectPageMeta";
import { isAutomatedSession } from "~/utils/tracking/isAutomated";
import { isCrawler } from "~/utils/tracking/isCrawler";

/**
 * Мету снимаем не сразу: Head обновляется после рендера страницы, а часть
 * заголовков приходит вместе с данными (каталог, карточка товара).
 */
const COLLECT_DELAY = 2000;

/**
 * Страницы, мета которых зависит от посетителя, а не от адреса: поиск — от
 * запроса, личный кабинет и корзина — от их состояния. В мониторинге они
 * давали ложные правки.
 */
const SKIPPED_PATH = /^\/(search|personal)(\/|$)/;

/**
 * Контакты и доставка на farkop.ru подставляют в H1 город, выбранный
 * посетителем (setTextCity с cityName). На поддоменах город берётся из домена,
 * там эти страницы отслеживаем.
 */
const CITY_PATH = /^\/(contacts|delivery)(\/|$)/;

/** Локальная разработка: прокси /database/ там нет, слать мету некуда */
function isLocalHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

/**
 * Отправка реальной меты открытой страницы в nestFarkopDatabase.
 *
 * Один запрос на переход: сервис считает его открытием страницы и, если мета
 * отличается от прошлой, пишет её в историю. Поэтому дата в истории — это дата
 * захода, на котором изменение заметили, а не дата правки меты.
 */
export function usePageMetaTracking(): void {
  const router = useRouter();
  // Последний отправленный снимок: effect срабатывает и на повторных рендерах
  // (и дважды в StrictMode), а открытие страницы должно считаться один раз.
  const sentKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !router.isReady) {
      return;
    }
    // Прогон e2e-тестов не должен накручивать открытия страниц.
    //
    // Краулеры отсекаются по той же причине и ещё по одной: они обходят
    // каталог пачками, упираются в лимит запросов DDoS-Guard и на каждой
    // странице получают 429. Мета, снятая ботом, для мониторинга всё равно
    // не нужна — она та же, что у живого посетителя.
    if (
      isAutomatedSession() ||
      isCrawler() ||
      isLocalHost(window.location.hostname)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      try {
        // Пагинация (?page=N) живёт по тому же адресу, что и первая страница,
        // а мета у неё с « - Страница N»: в мониторинге каждый переход между
        // страницами списка выглядел как правка меты.
        if (new URLSearchParams(window.location.search).has("page")) {
          return;
        }
        if (
          SKIPPED_PATH.test(window.location.pathname) ||
          (window.location.hostname === "farkop.ru" &&
            CITY_PATH.test(window.location.pathname))
        ) {
          return;
        }
        // Переводчик браузера (Chrome) переписывает текст страницы и ставит
        // <html> класс translated-ltr/rtl: H1 приходил бы на чужом языке.
        if (
          /\btranslated-(ltr|rtl)\b/.test(document.documentElement.className)
        ) {
          return;
        }
        const meta = collectPageMeta(document, window.location);
        // После перехода внутри сайта canonical части страниц собран из адреса
        // запроса данных (/_next/data/<buildId>/...json): он меняется с каждым
        // деплоем, и такой снимок в мониторинге — ложное изменение.
        if (meta.canonical.includes("/_next/")) {
          return;
        }
        const key = metaSnapshotKey(meta);
        if (sentKeyRef.current === key) {
          return;
        }
        sentKeyRef.current = key;
        // Ошибку проглотит axiosWrapper и отправит в GlitchTip: мониторинг
        // меты не должен влиять на страницу.
        Api.pageMetaTracking.track(meta);
      } catch (error) {
        console.warn("usePageMetaTracking error", error);
      }
    }, COLLECT_DELAY);

    // Начался переход: адрес в строке сменится раньше, чем DOM, и снимок
    // склеил бы адрес новой страницы с метой старой.
    const cancel = () => window.clearTimeout(timer);
    router.events.on("routeChangeStart", cancel);

    return () => {
      cancel();
      router.events.off("routeChangeStart", cancel);
    };
  }, [router.isReady, router.asPath]);
}
